// https://github.com/sersavan/shadcn-multi-select-component

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@src/lib/utils/cn";
import { Separator } from "@src/lib/ui/Separator";
import { selectTriggerClassName } from "@src/lib/ui/Select";
import { Badge } from "@src/lib/ui/Badge";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@src/lib/ui/Command";
import XIcon from "@src/lib/icons/12px/x";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";
import ChevronsOpenIcon from "@src/lib/icons/12px/chevrons-open";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import CircleXFillIcon from "@src/lib/icons/18px/circle-x-fill";
import Spinner from "@src/lib/ui/Spinner";

const multiSelectVariants = cva("m-1", {
  variants: {
    variant: {
      default: "border-foreground/10 text-foreground bg-card hover:bg-card/80",
      secondary:
        "border-foreground/10 bg-secondary text-foreground hover:bg-secondary/80",
      destructive:
        "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
      inverted: "inverted",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  onValueChange: (value: string[]) => void;
  defaultValue?: string[];
  placeholder?: string;
  maxCount?: number;
  dialogPopover?: boolean;
  asChild?: boolean;
  className?: string;
  contentClassName?: string;
  enableAll?: boolean;
  matchContentWidthToTrigger?: boolean;
  onSearchTermChange?: (term: string) => void;
  isLoading?: boolean;
  triggerLabel?: string;
  hideActions?: boolean;
}

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      placeholder = "Select options",
      maxCount = 3,
      dialogPopover = false,
      asChild = false,
      className,
      contentClassName,
      enableAll = true,
      matchContentWidthToTrigger = true,
      onSearchTermChange,
      isLoading = false,
      triggerLabel,
      hideActions = false,
      ...props
    },
    ref,
  ) => {
    const [selectedValues, setSelectedValues] =
      React.useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

    // Sync internal state with external defaultValue changes
    React.useEffect(() => {
      setSelectedValues(defaultValue);
    }, [defaultValue]);
    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };
    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };
    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };
    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };
    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };
    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map((option) => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }
    };
    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={dialogPopover}
      >
        <PopoverTrigger asChild>
          {/* A field trigger, not an action — style like an input, not a Button. */}
          <button
            ref={ref}
            type="button"
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              selectTriggerClassName,
              "max-w-full min-w-0 overflow-hidden whitespace-normal",
              triggerLabel
                ? "h-7 px-2.5 py-1"
                : selectedValues.length > 0
                  ? "h-auto min-h-7 p-1"
                  : "h-7 px-2.5",
              className,
            )}
          >
            {selectedValues.length > 0 ? (
              triggerLabel ? (
                <div className="flex items-center justify-between w-full min-w-0">
                  <span className="truncate font-normal">{triggerLabel}</span>
                  <ChevronsOpenIcon className="text-muted-foreground shrink-0" />
                </div>
              ) : (
                <div className="flex justify-between items-center w-full min-w-0">
                  <div className="flex flex-wrap items-center flex-1 min-w-0 overflow-hidden">
                    {selectedValues.slice(0, maxCount).map((value) => {
                      const option = options.find((o) => o.value === value);
                      if (!option) return null;
                      const IconComponent = option.icon;
                      return (
                        <Badge
                          key={value}
                          className={cn(
                            multiSelectVariants({ variant }),
                            "max-w-full min-w-0 px-1",
                          )}
                        >
                          {IconComponent && (
                            <IconComponent className="mr-1 h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                          <span className="truncate">{option.label}</span>
                          <CircleXFillIcon
                            className="ml-2 size-4 shrink-0 cursor-pointer text-gray-6"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleOption(value);
                            }}
                          />
                        </Badge>
                      );
                    })}
                    {selectedValues.length > maxCount && (
                      <Badge
                        className={cn(
                          "pl-2 pr-1 bg-transparent text-foreground border-foreground/1 hover:bg-transparent",
                          multiSelectVariants({ variant }),
                        )}
                      >
                        {`+ ${selectedValues.length - maxCount} more`}
                        <CircleXFillIcon
                          className="ml-2 size-4 cursor-pointer text-muted-foreground"
                          onClick={(event) => {
                            event.stopPropagation();
                            clearExtraOptions();
                          }}
                        />
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between shrink-0">
                    <XIcon
                      className="!size-3 mx-2 cursor-pointer text-muted-foreground"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClear();
                      }}
                    />
                    <Separator
                      orientation="vertical"
                      className="flex min-h-6 h-full"
                    />
                    <ChevronDownIcon className="!size-3 mx-2 cursor-pointer text-muted-foreground" />
                  </div>
                </div>
              )
            ) : (
              <div className="flex w-full min-w-0 items-center justify-between gap-2">
                <span className="truncate text-sm text-muted-foreground">
                  {placeholder}
                </span>
                <ChevronDownIcon className="!size-3 shrink-0 text-muted-foreground" />
              </div>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "p-0 min-h-[120px]",
            matchContentWidthToTrigger && "popover-content-w-full",
            contentClassName,
          )}
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <CommandInput
              placeholder="Search..."
              onValueChange={(v) => onSearchTermChange?.(v)}
              onKeyDown={handleInputKeyDown}
            />
            <CommandList>
              {!isLoading && <CommandEmpty>No results found.</CommandEmpty>}
              <CommandGroup>
                {enableAll && (
                  <CommandItem
                    key="all"
                    value="__all__"
                    onSelect={toggleAll}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "mr-2 flex size-4 items-center justify-center rounded border border-muted-foreground",
                        selectedValues.length === options.length
                          ? "bg-primary text-primary-foreground border-primary"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <CheckmarkIcon className="size-3 text-background" />
                    </div>
                    <span>Select All</span>
                  </CommandItem>
                )}
                {options.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => toggleOption(option.value)}
                      className="cursor-pointer"
                    >
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded border border-muted-foreground",
                          isSelected
                            ? "bg-primary text-primary-foreground border-primary"
                            : "opacity-50 [&_svg]:invisible",
                        )}
                      >
                        <CheckmarkIcon className="size-3 text-background" />
                      </div>
                      {option.icon && (
                        <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                      )}
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {isLoading && (
                <div className="flex items-center justify-center p-6">
                  <Spinner size={24} />
                </div>
              )}
              {!hideActions && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <div className="flex items-center justify-between">
                      {selectedValues.length > 0 && (
                        <>
                          <CommandItem
                            value="__clear__"
                            onSelect={handleClear}
                            className="flex-1 justify-center cursor-pointer"
                          >
                            Clear
                          </CommandItem>
                          <Separator
                            orientation="vertical"
                            className="flex min-h-6 h-full"
                          />
                        </>
                      )}
                      <CommandItem
                        value="__close__"
                        onSelect={() => setIsPopoverOpen(false)}
                        className="flex-1 justify-center cursor-pointer max-w-full"
                      >
                        Close
                      </CommandItem>
                    </div>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

MultiSelect.displayName = "MultiSelect";

export default MultiSelect;
